import { useMemo, useState } from "react";
import Section from "../components/Section";
import centers from "../data/centers.mumbai.json";
import doctors from "../data/doctors.mumbai.json";

import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// fix leaflet default icon in Vite
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

L.Marker.prototype.options.icon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

export default function TestingLocator() {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("doctors"); // toggle

  const dataset = useMemo(() => {
    const base = tab === "doctors" ? doctors : centers;
    const t = q.trim().toLowerCase();
    if (!t) return base;
    return base.filter(x =>
      (
        (x.name || "") +
        " " +
        (x.area || "") +
        " " +
        (x.full_address || "") +
        " " +
        (x.hospital || "") +
        " " +
        (x.location || "")
      )
        .toLowerCase()
        .includes(t)
    );
  }, [q, tab]);

  return (
    <Section
      eyebrow="Locator"
      title="Testing Centers & Doctors — Mumbai"
      subtitle="Search and view locations"
    >
      {/* Map */}
      <div className="rounded-2xl overflow-hidden border shadow-sm mb-4">
        {tab === "doctors" ? (
          <MapContainer center={[19.076, 72.8777]} zoom={11} style={{ height: "320px", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {dataset.map((d, i) =>
              typeof d.lat === "number" && typeof d.lng === "number" ? (
                <Marker key={i} position={[d.lat, d.lng]}>
                  <Popup>
                    <div style={{ font: "14px/1.4 system-ui" }}>
                      <div style={{ fontWeight: 600 }}>{d.name}</div>
                      <div>{d.location}</div>
                      <div>{d.hospital}</div>
                      <div>{d.consultation_fee}</div>
                      <a href={d.google_maps_link} target="_blank" rel="noreferrer">
                        Open in Google Maps →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ) : null
            )}
          </MapContainer>
        ) : (
          <iframe
            title="Mumbai Testing Centers"
            className="w-full h-80"
            loading="lazy"
            src="https://www.google.com/maps?q=Mumbai%20Metropolis%20Redcliffe%20Labs&output=embed"
          />
        )}
      </div>

      {/* Search + Tabs */}
      <div className="flex items-center gap-3 mb-3">
        <div className="inline-flex rounded-lg border p-1 bg-white">
          <button
            onClick={() => setTab("doctors")}
            className={`px-3 py-1 rounded-md text-sm ${
              tab === "doctors" ? "bg-rose-600 text-white" : "text-slate-700"
            }`}
          >
            Doctors
          </button>
          <button
            onClick={() => setTab("centers")}
            className={`px-3 py-1 rounded-md text-sm ${
              tab === "centers" ? "bg-rose-600 text-white" : "text-slate-700"
            }`}
          >
            Testing Centers
          </button>
        </div>

        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name, area, or address…"
          className="w-full md:w-1/2 rounded-lg border px-4 py-2 outline-none focus:border-rose-600"
        />
      </div>

      {/* List */}
      <ul className="grid md:grid-cols-2 gap-4">
        {dataset.map((item, i) => (
          <li key={i} className="rounded-xl border p-5 bg-white shadow-sm">
            <h3 className="font-semibold text-slate-900">{item.name}</h3>
            {item.full_address && (
              <div className="text-sm text-slate-600 mt-1">
                {item.full_address} — {item.area}
              </div>
            )}
            {item.location && (
              <div className="text-sm text-slate-600 mt-1">{item.location}</div>
            )}
            {item.hospital && (
              <div className="text-sm text-slate-600">{item.hospital}</div>
            )}
            {item.consultation_fee && (
              <div className="text-sm text-slate-600">Fee: {item.consultation_fee}</div>
            )}
            {item.phone && <div className="text-sm text-slate-600">Phone: {item.phone}</div>}
            <div className="flex flex-wrap gap-3 mt-3">
              <a
                className="text-rose-700 font-medium hover:underline"
                href={item.google_maps_link}
                target="_blank"
                rel="noreferrer"
              >
                Open in Maps →
              </a>
              {item.home_collection === "yes" && (
                <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Home collection
                </span>
              )}
              {item.accreditation && (
                <span className="text-xs px-2 py-1 rounded bg-slate-50 text-slate-700 border">
                  {item.accreditation}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}
